import { DEFAULT_SETTINGS } from "../engine/game.js";
import { SYSTEMS } from "../engine/counting.js";

/* Sanitize a player's saved default Rules (profiles.default_settings) before it
   ever reaches the engine. The allowed values mirror the settings UI controls
   and the server's fair-play bounds (supabase/functions/_shared/bounds.js), so
   an honest saved default round-trips unchanged, while a malformed or tampered
   blob harmlessly falls back to the app default per key. Personal-pref data
   only — it never affects validation, ranking, or other users. */

const BOOL = (v) => typeof v === "boolean";
const oneOf = (set) => (v) => set.includes(v);
const inRange = (lo, hi) => (v) =>
  typeof v === "number" && Number.isFinite(v) && v >= lo && v <= hi;

const VALID = {
  decks: oneOf([1, 2, 4, 6, 8]),
  h17: BOOL,
  das: BOOL,
  surrender: BOOL,
  rsa: BOOL,
  insurance: BOOL,
  coach: BOOL,
  checkReveals: BOOL,
  maxSplitHands: oneOf([2, 3, 4]),
  bjPay: oneOf([1.5, 1.2]),
  system: oneOf(SYSTEMS),
  pen: inRange(50, 90),
  unit: inRange(1, 1000),
  spread: oneOf([4, 8, 12, 16]),
};

/* Returns a full, valid settings object built from `raw` (each key taken only
   if it passes its validator, else the app default), or null when `raw` isn't a
   usable object. Callers do:
     sanitizeSettings(profile?.default_settings) ?? DEFAULT_SETTINGS  */
export function sanitizeSettings(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const clean = { ...DEFAULT_SETTINGS };
  for (const k of Object.keys(VALID)) {
    if (k in raw && VALID[k](raw[k])) clean[k] = raw[k];
  }
  return clean;
}
